import { ReactNode, useEffect, useRef, useState } from "react";
import { Box } from "rebass/styled-components";
import { useLocation } from "react-router-dom";

import { NAV_BAR, NAV_CATEGORIES, TNavIcon, visibleItems } from "../links";
import { NavItem } from "../MegaMenu";
import {
  AccordionBody,
  AccordionTrigger,
  ItemIcon,
  ItemLink,
  ItemTitleRow,
  MobileCloseButton,
  MobileDialog,
  MobileHeader,
} from "../styledComponents";

/**
 * Icons earn their place here and not in the desktop bar: rows are scanned
 * vertically, so the glyph is a fixed anchor the eye lands on before the text.
 */
const RowLabel = ({
  icon: Icon,
  children,
}: {
  icon?: TNavIcon;
  children: ReactNode;
}) => (
  <ItemTitleRow>
    {Icon && (
      <ItemIcon aria-hidden="true">
        <Icon />
      </ItemIcon>
    )}
    {children}
  </ItemTitleRow>
);

// Native <dialog>: focus trap, Esc, backdrop and aria-modal all come from the
// platform, so there is no trap code of our own to get wrong.
export const MobileNav = ({
  open,
  isLoggedIn,
  onClose,
}: {
  open: boolean;
  isLoggedIn: boolean;
  onClose: () => void;
}) => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const { pathname } = useLocation();
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog || typeof dialog.showModal !== "function") return;

    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  return (
    <MobileDialog
      ref={dialogRef}
      onClose={onClose}
      aria-label="Site navigation"
    >
      <MobileHeader>
        <MobileCloseButton onClick={onClose} aria-label="Close navigation">
          &times;
        </MobileCloseButton>
      </MobileHeader>
      {/* The bar's direct destinations, identical in both auth states. */}
      {NAV_BAR.filter(
        (
          entry,
        ): entry is {
          kind: "link";
          label: string;
          to: string;
          icon?: TNavIcon;
        } => entry.kind === "link",
      ).map((link) => (
        <AccordionTrigger
          key={link.to}
          as={ItemLink}
          to={link.to}
          onClick={onClose}
        >
          <RowLabel icon={link.icon}>{link.label}</RowLabel>
        </AccordionTrigger>
      ))}
      {NAV_CATEGORIES.map((category) => {
        const isOpen = expanded === category.key;

        return (
          <Box key={category.key}>
            <AccordionTrigger
              $open={isOpen}
              aria-expanded={isOpen}
              aria-controls={`nav-section-${category.key}`}
              onClick={() => setExpanded(isOpen ? null : category.key)}
            >
              <RowLabel icon={category.icon}>{category.label}</RowLabel>
              <span aria-hidden="true">{isOpen ? "−" : "+"}</span>
            </AccordionTrigger>
            {isOpen && (
              <AccordionBody
                id={`nav-section-${category.key}`}
                $art={category.art}
                $glass={category.glass}
              >
                {/* Same row the desktop panel renders, so icons, badges and
                    active state cannot drift between the two. */}
                {visibleItems(category.items, isLoggedIn).map((item) => (
                  <NavItem
                    key={item.title}
                    item={item}
                    pathname={pathname}
                    onNavigate={onClose}
                  />
                ))}
              </AccordionBody>
            )}
          </Box>
        );
      })}
    </MobileDialog>
  );
};
